import { useMemo, useState, type JSX, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request, requestBlob } from '../api/client';

type PeriodKind = 'daily' | 'weekly' | 'monthly' | 'custom';

interface InsightsPayloadV1 {
  top_perguntas: Array<{
    pergunta: string;
    frequencia: number;
    exemplos: string[];
  }>;
  nichos: Array<{
    nome: string;
    descricao: string;
    frequencia: number;
    exemplos_pergunta: string[];
  }>;
  ideias_produtos: Array<{
    titulo: string;
    descricao: string;
    justificativa: string;
    publico: string;
    preco_sugerido_brl: number | null;
  }>;
  ganchos_marketing: Array<{
    gancho: string;
    contexto: string;
    publico_alvo: string;
  }>;
  resumo_geral: string;
}

// Formato novo (29/09/2026). Espelho de apps/backend/src/insights/v2/tipos.ts.
interface ItemContado {
  texto: string;
  pessoas: number;
}
interface InsightsPayloadV2 {
  versao: 2;
  marca: string;
  idiomaCliente: string;
  base: {
    mensagens: number;
    pessoasQueEscreveram: number;
    pessoasComConversa: number;
    pessoasComPedidoNovo: number;
    pedidosDeRotina: number;
  };
  resumo: string[];
  temas: Array<{ codigo: string; rotulo: string; principal: number; aparece: number }>;
  porTema?: Array<{
    codigo: string;
    rotulo: string;
    pessoas: number;
    resumo: string;
    desejos: string[];
    frases: string[];
  }>;
  perfil: ItemContado[];
  oportunidades: Array<{
    titulo: string;
    o_que_pedem: string;
    produto: string;
    cuidado: string;
    pessoas: number;
    frases: string[];
  }>;
  angulos: Array<{
    nome: string;
    para_quem: string;
    por_que: string;
    ganchos: string[];
    frases: string[];
    pessoas: number;
  }>;
  palavras: string[];
  objecoes: Array<{ objecao: string; como_desarmar: string; pessoas: number }>;
  nao_usar: string[];
  alertas: ItemContado[];
  crises: number;
  metodo: string;
}

type InsightsPayload = InsightsPayloadV1 | InsightsPayloadV2;

function ehV2(p: InsightsPayload): p is InsightsPayloadV2 {
  return (p as InsightsPayloadV2).versao === 2;
}

interface RunListItem {
  id: string;
  generatedAt: string;
  periodKind: PeriodKind;
  periodStart: string;
  periodEnd: string;
  status: 'pending' | 'running' | 'done' | 'failed';
  conversasAnalisadas: number;
  mensagensAnalisadas: number;
  llmCusto: number | null;
  triggeredBy: string | null;
  errorMessage: string | null;
}

interface RunDetail extends RunListItem {
  insights: InsightsPayload | null;
  llmModelo: string | null;
}

const PERIOD_LABEL: Record<PeriodKind, string> = {
  daily: 'Últimas 24h',
  weekly: 'Últimos 7 dias',
  monthly: 'Últimos 30 dias',
  custom: 'Customizado',
};

const STATUS_LABEL: Record<RunListItem['status'], string> = {
  pending: 'pendente',
  running: 'rodando',
  done: 'pronto',
  failed: 'falhou',
};

const STATUS_COLOR: Record<RunListItem['status'], string> = {
  pending: 'text-sara-text-muted',
  running: 'text-sara-accent',
  done: 'text-sara-success',
  failed: 'text-sara-danger',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDateOnly(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
  });
}

function formatBRL(brl: number | null): string {
  if (brl === null) return '-';
  return brl.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatUsd(usd: number | null): string {
  if (usd === null) return '-';
  return `US$ ${usd.toFixed(4)}`;
}

export function InsightsPage(): JSX.Element {
  const [filterKind, setFilterKind] = useState<PeriodKind | ''>('');
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const list = useQuery<RunListItem[]>({
    queryKey: ['insights-runs', filterKind],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filterKind) params.set('periodKind', filterKind);
      params.set('limit', '50');
      return request<RunListItem[]>(`/admin/insights?${params.toString()}`);
    },
    refetchInterval: 15_000,
  });

  // Auto-seleciona a primeira run "done" assim que a lista carrega.
  const autoSelectedId = useMemo(() => {
    if (selectedRunId) return selectedRunId;
    return list.data?.find((r) => r.status === 'done')?.id ?? null;
  }, [selectedRunId, list.data]);

  // Histórico agrupado por mês (organiza a lista longa).
  const historicoPorMes = useMemo(() => {
    const by = new Map<string, RunListItem[]>();
    for (const r of list.data ?? []) {
      const mes = new Date(r.generatedAt).toLocaleDateString('pt-BR', {
        month: 'long',
        year: 'numeric',
        timeZone: 'America/Sao_Paulo',
      });
      const arr = by.get(mes);
      if (arr) arr.push(r);
      else by.set(mes, [r]);
    }
    return Array.from(by.entries());
  }, [list.data]);

  const detail = useQuery<RunDetail>({
    queryKey: ['insights-run', autoSelectedId],
    queryFn: () => request<RunDetail>(`/admin/insights/${autoSelectedId}`),
    enabled: !!autoSelectedId,
    // Rodada pedida agora fica 'pending'/'running' por minutos: a tela se
    // atualiza sozinha até ela terminar.
    refetchInterval: (q) =>
      q.state.data?.status === 'pending' || q.state.data?.status === 'running' ? 10_000 : false,
  });

  const generate = useMutation({
    mutationFn: (periodKind: PeriodKind) =>
      request<{ runId: string; status: string }>('/admin/insights/generate', {
        method: 'POST',
        body: { periodKind },
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['insights-runs'] });
      // Seleciona automaticamente a recém-criada quando aparecer.
      setSelectedRunId(data.runId);
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'erro';
      alert(`Falha ao gerar insights: ${msg}`);
    },
  });

  return (
    <section aria-labelledby="insights-heading" className="space-y-6">
      <header>
        <h1 id="insights-heading" className="h1">
          Insights de produto
        </h1>
        <p className="muted text-sm mt-1">
          O que as clientes pedem e a gente ainda não vende, com ângulos de anúncio e frases reais
          para o time de copy. Lê cada pessoa inteira e conta uma vez por pessoa. Sai sozinho às
          03:00: o de 24h todo dia, o de 7 dias às segundas, o de 30 dias no dia 1.
        </p>
      </header>

      <div className="card space-y-3">
        <h2 className="text-base font-semibold">Gerar nova análise</h2>
        <p className="text-sm text-sara-text-muted">
          Leva de 2 a 10 minutos, conforme o período. Pode sair da tela: o resultado aparece no
          histórico ao lado quando ficar pronto. Custo aproximado: centavos para 24h, poucos dólares
          para 30 dias.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-primary text-sm"
            onClick={() => generate.mutate('daily')}
            disabled={generate.isPending}
          >
            {generate.isPending && generate.variables === 'daily'
              ? 'Pedindo…'
              : 'Gerar: últimas 24h'}
          </button>
          <button
            type="button"
            className="btn-primary text-sm"
            onClick={() => generate.mutate('weekly')}
            disabled={generate.isPending}
          >
            {generate.isPending && generate.variables === 'weekly'
              ? 'Pedindo…'
              : 'Gerar: últimos 7 dias'}
          </button>
          <button
            type="button"
            className="btn-primary text-sm"
            onClick={() => generate.mutate('monthly')}
            disabled={generate.isPending}
          >
            {generate.isPending && generate.variables === 'monthly'
              ? 'Pedindo…'
              : 'Gerar: últimos 30 dias'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 lg:items-start">
        <aside className="space-y-3 lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto lg:pr-1">
          <div className="flex flex-wrap gap-1">
            <button
              type="button"
              className={`btn-ghost text-xs ${!filterKind ? 'bg-sara-accent/20 border-sara-accent' : ''}`}
              onClick={() => setFilterKind('')}
            >
              Todos
            </button>
            {(['daily', 'weekly', 'monthly'] as const).map((k) => (
              <button
                key={k}
                type="button"
                className={`btn-ghost text-xs ${filterKind === k ? 'bg-sara-accent/20 border-sara-accent' : ''}`}
                onClick={() => setFilterKind(k)}
              >
                {PERIOD_LABEL[k]}
              </button>
            ))}
          </div>

          <h2 className="text-sm font-semibold text-sara-text-muted">Histórico</h2>

          {list.isLoading && <p className="text-sm text-sara-text-muted">Carregando…</p>}
          {list.isError && (
            <p className="text-sm text-sara-danger">
              Erro carregando histórico.{' '}
              <button type="button" className="underline" onClick={() => void list.refetch()}>
                Tentar de novo
              </button>
            </p>
          )}
          {list.data?.length === 0 && (
            <p className="text-sm text-sara-text-muted">
              Nenhuma análise ainda. Clique em &quot;Gerar&quot; pra criar a primeira.
            </p>
          )}
          <div className="space-y-3">
            {historicoPorMes.map(([mes, runs]) => (
              <div key={mes} className="space-y-2">
                <h3 className="pt-1 text-[11px] font-medium uppercase tracking-wide text-sara-text-muted">
                  {mes} <span className="text-sara-text/40">({runs.length})</span>
                </h3>
                <ul className="space-y-2">
                  {runs.map((r) => {
                    const isSelected = autoSelectedId === r.id;
                    return (
                      <li key={r.id}>
                        <button
                          type="button"
                          onClick={() => setSelectedRunId(r.id)}
                          className={`w-full text-left card-hover ${isSelected ? 'border-sara-accent ring-1 ring-sara-accent' : ''}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-medium text-sara-text">
                              {PERIOD_LABEL[r.periodKind]}
                            </span>
                            <span className={`text-xs ${STATUS_COLOR[r.status]}`}>
                              {STATUS_LABEL[r.status]}
                            </span>
                          </div>
                          <div className="mt-1 text-xs text-sara-text-muted">
                            {formatDateOnly(r.periodStart)} a {formatDateOnly(r.periodEnd)}
                          </div>
                          <div className="mt-1 text-[11px] text-sara-text/50">
                            gerado {formatDate(r.generatedAt)}
                            {' · '}
                            {r.conversasAnalisadas} pessoas · {r.mensagensAnalisadas} mensagens
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </aside>

        <main>
          {!autoSelectedId && !list.isLoading && (
            <div className="card text-sm text-sara-text-muted">
              Selecione uma análise no histórico ou gere uma nova pra começar.
            </div>
          )}

          {autoSelectedId && detail.isLoading && (
            <div className="card animate-pulse">
              <div className="h-4 bg-sara-surface-hover rounded w-32 mb-2" />
              <div className="h-3 bg-sara-surface-hover rounded w-48" />
            </div>
          )}

          {detail.data && <RunDetailView run={detail.data} />}
        </main>
      </div>
    </section>
  );
}

function RunDetailView({ run }: { run: RunDetail }): JSX.Element {
  if (run.status === 'failed') {
    return (
      <div className="card border-sara-danger">
        <p className="text-sara-danger font-medium">Análise falhou</p>
        <p className="text-sm text-sara-text-muted mt-1">
          {run.errorMessage || 'Sem detalhes do erro.'}
        </p>
      </div>
    );
  }

  if (run.status === 'pending' || run.status === 'running') {
    return (
      <div className="card">
        <p className="text-sm text-sara-text-muted">
          Lendo as conversas ({STATUS_LABEL[run.status]}). Leva de 2 a 10 minutos; esta tela se
          atualiza sozinha.
        </p>
      </div>
    );
  }

  const insights = run.insights;
  if (!insights) {
    return (
      <div className="card">
        <p className="text-sm text-sara-text-muted">
          {run.errorMessage || 'Análise sem insights gerados.'}
        </p>
      </div>
    );
  }

  if (ehV2(insights)) return <RelatorioV2 run={run} p={insights} />;

  if (run.mensagensAnalisadas === 0) {
    return (
      <div className="card">
        <p className="text-sm text-sara-text-muted">
          Sem mensagens substantivas no período pra análise.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
          <h2 className="text-lg font-semibold">{PERIOD_LABEL[run.periodKind]}</h2>
          <span className="text-xs text-sara-text-muted">
            {formatDateOnly(run.periodStart)} a {formatDateOnly(run.periodEnd)} · gerado{' '}
            {formatDate(run.generatedAt)}
          </span>
        </div>
        <p className="text-sm leading-relaxed whitespace-pre-line">{insights.resumo_geral}</p>
        <div className="mt-3 text-xs text-sara-text/50">
          {run.mensagensAnalisadas} mensagens · {run.conversasAnalisadas} conversas
          {run.llmModelo ? ` · ${run.llmModelo}` : ''}
          {run.llmCusto !== null ? ` · ${formatUsd(run.llmCusto)}` : ''}
        </div>
        <ExportBar runId={run.id} />
      </div>

      <Section
        title={`Top ${insights.top_perguntas.length} perguntas mais feitas`}
        empty="Nenhuma pergunta extraída."
        items={insights.top_perguntas}
        renderItem={(p, idx) => <ExpandableQuestion p={p} idx={idx} runId={run.id} />}
      />

      <Section
        title="Nichos / temas dominantes"
        empty="Nenhum nicho extraído."
        items={insights.nichos}
        renderItem={(n) => <ExpandableNicho n={n} />}
      />

      <Section
        title="Ideias de produtos novos"
        empty="Nenhuma ideia gerada."
        items={insights.ideias_produtos}
        renderItem={(p) => (
          <div className="card-hover">
            <div className="flex items-baseline justify-between gap-2">
              <h3 className="text-base font-semibold">{p.titulo}</h3>
              <span className="text-sm font-medium text-sara-accent">
                {formatBRL(p.preco_sugerido_brl)}
              </span>
            </div>
            <p className="text-sm mt-1">{p.descricao}</p>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-sara-text-muted">Por que: </span>
                <span>{p.justificativa}</span>
              </div>
              <div>
                <span className="text-sara-text-muted">Pra quem: </span>
                <span>{p.publico}</span>
              </div>
            </div>
          </div>
        )}
      />

      <Section
        title="Ganchos de marketing / copy"
        empty="Nenhum gancho gerado."
        items={insights.ganchos_marketing}
        renderItem={(g) => (
          <div className="card-hover">
            <p className="text-base font-medium">&ldquo;{g.gancho}&rdquo;</p>
            <p className="text-sm text-sara-text-muted mt-1">{g.contexto}</p>
            <p className="text-xs text-sara-text/70 mt-1">Público: {g.publico_alvo}</p>
          </div>
        )}
      />
    </div>
  );
}

// ============== Relatório v2 (29/09/2026) ==============

const gente = (n: number): string =>
  `${n.toLocaleString('pt-BR')} ${n === 1 ? 'pessoa' : 'pessoas'}`;

function RelatorioV2({ run, p }: { run: RunDetail; p: InsightsPayloadV2 }): JSX.Element {
  const maxTema = Math.max(1, ...p.temas.map((t) => t.aparece));
  return (
    <div className="space-y-8">
      <div className="card space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-sara-accent">
              {p.marca}
            </p>
            <h2 className="text-xl font-semibold mt-1">
              O que elas pedem e a gente ainda não vende
            </h2>
          </div>
          <span className="text-xs text-sara-text-muted">
            {PERIOD_LABEL[run.periodKind]} · {formatDateOnly(run.periodStart)} a{' '}
            {formatDateOnly(run.periodEnd)} · gerado {formatDate(run.generatedAt)}
          </span>
        </div>
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Numero rotulo="mensagens das clientes" valor={p.base.mensagens} />
          <Numero rotulo="pessoas escreveram" valor={p.base.pessoasQueEscreveram} />
          <Numero rotulo="com conversa de verdade" valor={p.base.pessoasComConversa} />
          <Numero
            rotulo="pediram algo que não vendemos"
            valor={p.base.pessoasComPedidoNovo}
            destaque
          />
        </dl>
        <p className="text-xs text-sara-text-muted">
          {p.base.pedidosDeRotina.toLocaleString('pt-BR')} pedidos do que já entregamos (como a
          carta do dia) ficaram de fora: aparecer muito ali é esperado e não é descoberta.
          {run.llmCusto !== null ? ` Custo desta análise: ${formatUsd(run.llmCusto)}.` : ''}
        </p>
        <ExportBar runId={run.id} />
      </div>

      {p.resumo.length > 0 && (
        <BlocoV2 titulo="Em 30 segundos">
          <ol className="card space-y-3 list-none">
            {p.resumo.map((r, i) => (
              <li key={i} className="flex gap-3 text-sm leading-relaxed">
                <span className="font-semibold text-sara-accent tabular-nums">{i + 1}</span>
                <span>{r}</span>
              </li>
            ))}
          </ol>
        </BlocoV2>
      )}

      {p.porTema && p.porTema.length > 0 && (
        <BlocoV2
          titulo="O que disseram sobre cada assunto"
          nota="Do assunto com mais pessoas para o com menos. Frases reais, com nomes, lugares e valores retirados."
        >
          <div className="space-y-4">
            {p.porTema.map((t) => (
              <article key={t.codigo} className="card grid gap-4 md:grid-cols-2">
                <div className="space-y-2 min-w-0">
                  <span className="badge-accent">{gente(t.pessoas)}</span>
                  <h3 className="text-base font-semibold">{t.rotulo}</h3>
                  {t.resumo && <p className="text-sm">{t.resumo}</p>}
                  {t.desejos.length > 0 && (
                    <div className="rounded-lg bg-sara-accent/10 px-3 py-2 text-sm">
                      <span className="block text-[11px] font-semibold uppercase tracking-wide text-sara-accent">
                        O que querem
                      </span>
                      {t.desejos.join('; ')}
                    </div>
                  )}
                </div>
                <Citacoes frases={t.frases} />
              </article>
            ))}
          </div>
        </BlocoV2>
      )}

      {p.oportunidades.length > 0 && (
        <BlocoV2
          titulo="O que dá para vender, além do que já entregamos"
          nota="Da maior para a menor quantidade de pessoas. O número é contado por pessoa, não é estimativa."
        >
          <div className="space-y-4">
            {p.oportunidades.map((o, i) => (
              <article key={i} className="card grid gap-4 md:grid-cols-2">
                <div className="space-y-2 min-w-0">
                  <span className="badge-accent">{gente(o.pessoas)}</span>
                  <h3 className="text-base font-semibold">{o.titulo}</h3>
                  {o.o_que_pedem && <p className="text-sm">{o.o_que_pedem}</p>}
                  {o.produto && (
                    <div className="rounded-lg bg-sara-accent/10 px-3 py-2 text-sm">
                      <span className="block text-[11px] font-semibold uppercase tracking-wide text-sara-accent">
                        Produto possível
                      </span>
                      {o.produto}
                    </div>
                  )}
                  {o.cuidado && (
                    <div className="rounded-lg bg-sara-danger/10 px-3 py-2 text-sm">
                      <span className="font-semibold text-sara-danger">Cuidado: </span>
                      {o.cuidado}
                    </div>
                  )}
                </div>
                <Citacoes frases={o.frases} />
              </article>
            ))}
          </div>
        </BlocoV2>
      )}

      {p.angulos.length > 0 && (
        <BlocoV2
          titulo="Ângulos de anúncio"
          nota={`As linhas destacadas são pontos de partida em ${p.idiomaCliente} para o time de copy. Toque para copiar.`}
        >
          <div className="grid gap-4 md:grid-cols-2">
            {p.angulos.map((a, i) => (
              <article key={i} className="card space-y-2 min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-sara-text-muted">
                  {a.para_quem}
                </p>
                <h3 className="text-base font-semibold">{a.nome}</h3>
                {a.por_que && <p className="text-sm text-sara-text-muted">{a.por_que}</p>}
                <div className="space-y-1.5">
                  {a.ganchos.map((g, k) => (
                    <Copiavel key={k} texto={g} />
                  ))}
                </div>
                <Citacoes frases={a.frases} />
              </article>
            ))}
          </div>
        </BlocoV2>
      )}

      {p.palavras.length > 0 && (
        <BlocoV2
          titulo={`Palavras delas (em ${p.idiomaCliente})`}
          nota="Copiadas literalmente das conversas."
        >
          <div className="flex flex-wrap gap-2">
            {p.palavras.map((w) => (
              <span
                key={w}
                className="rounded-full border border-sara-border bg-sara-surface px-3 py-1 text-sm italic"
              >
                {w}
              </span>
            ))}
          </div>
        </BlocoV2>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {p.temas.length > 0 && (
          <BlocoV2
            titulo="Do que elas falam"
            nota="Pessoas por tema. A barra mostra quem cita o tema de qualquer forma."
          >
            <div className="card overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-sara-text-muted">
                    <th className="py-1.5 pr-2 font-medium">Tema</th>
                    <th className="py-1.5 pr-2 font-medium">Principal</th>
                    <th className="py-1.5 font-medium">Aparece</th>
                  </tr>
                </thead>
                <tbody>
                  {p.temas.map((t) => (
                    <tr key={t.codigo} className="border-t border-sara-border/60">
                      <td className="py-1.5 pr-2">{t.rotulo}</td>
                      <td className="py-1.5 pr-2 tabular-nums">{t.principal}</td>
                      <td className="py-1.5 tabular-nums whitespace-nowrap">
                        <span
                          className="inline-block h-2 rounded bg-sara-accent align-middle mr-2"
                          style={{ width: `${Math.max(4, (t.aparece / maxTema) * 80)}px` }}
                        />
                        {t.aparece}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </BlocoV2>
        )}
        <div className="space-y-6">
          {p.perfil.length > 0 && (
            <BlocoV2
              titulo="Quem está do outro lado"
              nota="Pelas pistas que elas mesmas dão. Contagem mínima."
            >
              <ListaContada itens={p.perfil} />
            </BlocoV2>
          )}
          {p.objecoes.length > 0 && (
            <BlocoV2 titulo="Objeções que o anúncio precisa desarmar">
              <ul className="card space-y-3 text-sm">
                {p.objecoes.map((o, i) => (
                  <li key={i}>
                    <span className="font-semibold">{o.objecao}</span>{' '}
                    <span className="text-sara-text-muted">({gente(o.pessoas)})</span>
                    <p className="text-sara-text-muted">{o.como_desarmar}</p>
                  </li>
                ))}
              </ul>
            </BlocoV2>
          )}
        </div>
      </div>

      {p.nao_usar.length > 0 && (
        <BlocoV2 titulo="O que não usar em anúncio">
          <ul className="card list-disc pl-9 space-y-1.5 text-sm">
            {p.nao_usar.map((x, i) => (
              <li key={i}>{x}</li>
            ))}
          </ul>
        </BlocoV2>
      )}

      {(p.alertas.length > 0 || p.crises > 0) && (
        <BlocoV2
          titulo="Apareceu na leitura e não é assunto de marketing"
          nota="Defeitos que as clientes relataram. Vai para quem cuida da operação."
        >
          <ul className="card border-sara-warning/50 space-y-2 text-sm">
            {p.alertas.map((a, i) => (
              <li key={i}>
                {a.texto} <span className="text-sara-text-muted">({gente(a.pessoas)})</span>
              </li>
            ))}
            {p.crises > 0 && (
              <li>
                {gente(p.crises)} em crise (risco à própria pessoa ou adoecimento). Ficaram fora de
                oferta e de citação.
              </li>
            )}
          </ul>
        </BlocoV2>
      )}

      <p className="text-xs text-sara-text-muted">Como foi feito: {p.metodo}</p>
    </div>
  );
}

function Numero({
  rotulo,
  valor,
  destaque,
}: {
  rotulo: string;
  valor: number;
  destaque?: boolean;
}): JSX.Element {
  return (
    <div className="rounded-lg bg-sara-surface-hover/60 px-3 py-2">
      <dd className={`text-xl font-semibold tabular-nums ${destaque ? 'text-sara-accent' : ''}`}>
        {valor.toLocaleString('pt-BR')}
      </dd>
      <dt className="text-xs text-sara-text-muted">{rotulo}</dt>
    </div>
  );
}

function BlocoV2({
  titulo,
  nota,
  children,
}: {
  titulo: string;
  nota?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold">{titulo}</h2>
        {nota && <p className="text-xs text-sara-text-muted mt-0.5">{nota}</p>}
      </div>
      {children}
    </section>
  );
}

function Citacoes({ frases }: { frases: string[] }): JSX.Element | null {
  if (frases.length === 0) return null;
  return (
    <div className="space-y-2 min-w-0">
      {frases.map((f, i) => (
        <blockquote
          key={i}
          className="border-l-2 border-sara-accent-soft pl-3 text-sm italic text-sara-text/90"
        >
          &ldquo;{f}&rdquo;
        </blockquote>
      ))}
    </div>
  );
}

function ListaContada({
  itens,
}: {
  itens: Array<{ texto: string; pessoas: number }>;
}): JSX.Element {
  return (
    <ul className="card space-y-1.5 text-sm">
      {itens.map((x, i) => (
        <li key={i} className="flex justify-between gap-3">
          <span>{x.texto}</span>
          <span className="tabular-nums text-sara-text-muted whitespace-nowrap">
            {gente(x.pessoas)}
          </span>
        </li>
      ))}
    </ul>
  );
}

function Copiavel({ texto }: { texto: string }): JSX.Element {
  const [copiado, setCopiado] = useState(false);
  const copiar = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1500);
    } catch {
      /* navegador sem permissão de copiar: o texto continua selecionável */
    }
  };
  return (
    <button
      type="button"
      onClick={() => void copiar()}
      className="block w-full text-left rounded-lg bg-sara-warning/15 px-3 py-2 text-sm font-medium hover:bg-sara-warning/25"
      title="Copiar"
    >
      {texto}
      <span className="ml-2 text-[11px] font-normal text-sara-text-muted">
        {copiado ? 'copiado' : 'copiar'}
      </span>
    </button>
  );
}

function ExportBar({ runId }: { runId: string }): JSX.Element {
  const [downloading, setDownloading] = useState<string | null>(null);

  const download = async (format: 'txt' | 'md' | 'html' | 'pdf' | 'docx'): Promise<void> => {
    setDownloading(format);
    try {
      const blob = await requestBlob(`/admin/insights/${runId}/export?format=${format}`);
      const url = URL.createObjectURL(blob);
      // pdf e html abrem em nova aba (preview); txt/md/docx forçam download
      if (format === 'pdf' || format === 'html') {
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } else {
        const a = document.createElement('a');
        a.href = url;
        const ext = format === 'docx' ? 'doc' : format;
        a.download = `insights-${runId}.${ext}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error(err);
      alert(`Erro exportando: ${err instanceof Error ? err.message : 'erro'}`);
    } finally {
      setDownloading(null);
    }
  };

  const FORMATS: Array<{ key: 'txt' | 'md' | 'html' | 'pdf' | 'docx'; label: string }> = [
    { key: 'pdf', label: '📄 PDF' },
    { key: 'html', label: '🌐 HTML' },
    { key: 'docx', label: '📝 DOCX/Word' },
    { key: 'md', label: 'Markdown' },
    { key: 'txt', label: 'TXT' },
  ];

  return (
    <div className="mt-3 pt-3 border-t border-sara-divider/40">
      <span className="text-xs text-sara-text-muted">Exportar:</span>
      <div className="inline-flex flex-wrap gap-1 ml-2">
        {FORMATS.map((f) => (
          <button
            key={f.key}
            type="button"
            className="btn-ghost text-xs"
            disabled={downloading === f.key}
            onClick={() => void download(f.key)}
            title={
              f.key === 'docx'
                ? 'Salva como .doc: o Word abre direto, e dá pra "Salvar como .docx" depois'
                : undefined
            }
          >
            {downloading === f.key ? '…' : f.label}
          </button>
        ))}
      </div>
    </div>
  );
}

interface OcorrenciaReal {
  conteudo: string;
  enviadaEm: string;
  clienteNome: string;
  clienteTelefone: string;
  conversaId: string;
}

interface OcorrenciasResponse {
  pergunta: string;
  keywords: string[];
  ocorrencias: OcorrenciaReal[];
}

function ExpandableQuestion({
  p,
  idx,
  runId,
}: {
  p: { pergunta: string; frequencia: number; exemplos: string[] };
  idx: number;
  runId: string;
}): JSX.Element {
  const [showAll, setShowAll] = useState(false);
  const initial = p.exemplos.slice(0, 2);

  // Busca ocorrências reais sob demanda: só dispara quando o usuário
  // expande "Ver todas as N ocorrências reais".
  const ocorrencias = useQuery<OcorrenciasResponse>({
    queryKey: ['insights-ocorrencias', runId, idx],
    queryFn: () =>
      request<OcorrenciasResponse>(
        `/admin/insights/${runId}/perguntas/${idx}/ocorrencias?limit=50`,
      ),
    enabled: showAll,
    staleTime: 5 * 60_000,
  });

  return (
    <div className="card-hover">
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-semibold text-sara-accent">{idx + 1}</span>
        <h3 className="text-base font-medium">{p.pergunta}</h3>
        <span className="ml-auto text-xs text-sara-text-muted">~{p.frequencia} ocorrências</span>
      </div>
      {p.exemplos.length > 0 && !showAll && (
        <ul className="mt-2 space-y-1 text-xs text-sara-text-muted italic">
          {initial.map((ex, i) => (
            <li key={i}>&ldquo;{ex}&rdquo;</li>
          ))}
        </ul>
      )}

      <button
        type="button"
        className="mt-2 text-xs text-sara-accent hover:underline"
        onClick={() => setShowAll(!showAll)}
      >
        {showAll ? '▲ Recolher' : `▼ Ver todas as ocorrências reais no DB`}
      </button>

      {showAll && (
        <div className="mt-3 space-y-2">
          {ocorrencias.isLoading && (
            <p className="text-xs text-sara-text-muted">Buscando ocorrências reais…</p>
          )}
          {ocorrencias.isError && (
            <p className="text-xs text-sara-danger">
              Erro buscando ocorrências.{' '}
              <button
                type="button"
                className="underline"
                onClick={() => void ocorrencias.refetch()}
              >
                Tentar de novo
              </button>
            </p>
          )}
          {ocorrencias.data && (
            <>
              <p className="text-xs text-sara-text-muted">
                <strong>{ocorrencias.data.ocorrencias.length}</strong> mensagens reais encontradas
                no período (busca por:{' '}
                {ocorrencias.data.keywords.map((k) => (
                  <span
                    key={k}
                    className="inline-block px-1.5 py-0.5 mx-0.5 rounded bg-sara-accent/10 text-sara-accent text-[10px]"
                  >
                    {k}
                  </span>
                ))}
                )
              </p>
              {ocorrencias.data.ocorrencias.length === 0 ? (
                <p className="text-xs text-sara-text-muted italic">
                  Nenhuma mensagem real casou com essas palavras-chave. A "frequência" do topo é uma
                  estimativa do LLM, e pode haver perguntas similares com vocabulário diferente.
                </p>
              ) : (
                <ul className="space-y-1.5 max-h-96 overflow-y-auto">
                  {ocorrencias.data.ocorrencias.map((o, i) => (
                    <li
                      key={i}
                      className="bg-sara-surface-hover/40 rounded px-3 py-2 text-xs border border-sara-divider/40"
                    >
                      <div className="flex items-baseline justify-between gap-2 mb-1">
                        <span className="font-medium text-sara-text">{o.clienteNome}</span>
                        <span className="text-[10px] text-sara-text-muted">
                          {o.clienteTelefone} ·{' '}
                          {new Date(o.enviadaEm).toLocaleString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                      <p className="text-sara-text/90 italic">&ldquo;{o.conteudo}&rdquo;</p>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ExpandableNicho({
  n,
}: {
  n: {
    nome: string;
    descricao: string;
    frequencia: number;
    exemplos_pergunta: string[];
  };
}): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const initial = n.exemplos_pergunta.slice(0, 3);
  const rest = n.exemplos_pergunta.slice(3);
  return (
    <div className="card-hover">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-base font-medium">{n.nome}</h3>
        <span className="text-xs text-sara-text-muted">~{n.frequencia} ocorrências</span>
      </div>
      <p className="text-sm text-sara-text-muted mt-1">{n.descricao}</p>
      {n.exemplos_pergunta.length > 0 && (
        <>
          <ul className="mt-2 space-y-1 text-xs text-sara-text/70 list-disc list-inside">
            {(expanded ? n.exemplos_pergunta : initial).map((ex, i) => (
              <li key={i}>{ex}</li>
            ))}
          </ul>
          {rest.length > 0 && (
            <button
              type="button"
              className="mt-2 text-xs text-sara-accent hover:underline"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded
                ? '▲ Recolher'
                : `▼ Ver mais ${rest.length} pergunta${rest.length > 1 ? 's' : ''}`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

function Section<T>({
  title,
  empty,
  items,
  renderItem,
}: {
  title: string;
  empty: string;
  items: T[];
  renderItem: (item: T, idx: number) => JSX.Element;
}): JSX.Element {
  return (
    <div>
      <h2 className="text-base font-semibold mb-3 text-sara-text-muted">{title}</h2>
      {items.length === 0 ? (
        <p className="text-sm text-sara-text-muted">{empty}</p>
      ) : (
        <div className="space-y-3">
          {items.map((item, idx) => (
            <div key={idx}>{renderItem(item, idx)}</div>
          ))}
        </div>
      )}
    </div>
  );
}
